import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  ChefHat, Settings, Users, Compass, MapPin, KeyRound, Loader2,
  Sparkles, Ship, ArrowRight, Clock, DollarSign, Utensils,
  Trees, Waves, Bird, Mountain, Sun
} from "lucide-react";
import { getHotels } from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Meal data
const meals = [
  { name: "Breakfast", time: "6:00 - 8:00 AM", price: 12 },
  { name: "Lunch", time: "12:30 - 1:30 PM", price: 15 },
  { name: "Dinner", time: "6:30 - 7:30 PM", price: 18 },
];

// Tour categories
const tourCategories = [
  {
    title: "Corcovado",
    subtitle: "National Park",
    description: "Trek through the most biodiverse place on Earth",
    icon: Mountain,
    image: "https://images.pexels.com/photos/2307634/pexels-photo-2307634.jpeg?auto=compress&cs=tinysrgb&w=1200"
  },
  {
    title: "Ocean",
    subtitle: "Adventures",
    description: "Snorkeling, diving, and whale watching",
    icon: Waves,
    image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80"
  },
  {
    title: "Wildlife",
    subtitle: "Encounters",
    description: "Night tours, birdwatching, and mangroves",
    icon: Bird,
    image: "https://images.pexels.com/photos/29635792/pexels-photo-29635792.jpeg?auto=compress&cs=tinysrgb&w=1200"
  }
];

export default function LandingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hotelId = searchParams.get("hotel");
  const cabinId = searchParams.get("cabin");
  
  const [hotels, setHotels] = useState([]);
  const [reservationCode, setReservationCode] = useState("");
  const [lookingUp, setLookingUp] = useState(false);

  useEffect(() => {
    // If hotel and cabin are in URL, redirect to reservation page
    if (hotelId && cabinId) {
      navigate(`/reserve/${hotelId}/${cabinId}`);
      return;
    }
    
    // Load hotels
    getHotels().then(res => {
      if (res.data) setHotels(res.data);
    });
  }, [hotelId, cabinId, navigate]);

  const handleCodeLookup = async (e) => {
    e.preventDefault();
    if (!reservationCode.trim()) {
      toast.error("Please enter your reservation code");
      return;
    }
    
    try {
      setLookingUp(true);
      const res = await fetch(`${API_URL}/api/reservation-by-code/${reservationCode}`);
      if (res.ok) {
        const data = await res.json();
        toast.success("Reservation found!");
        navigate(`/reserve/${data.hotel_id}/${data.cabin_id}`);
      } else {
        toast.error("Reservation code not found. Please check and try again.");
      }
    } catch (error) {
      console.error("Lookup error:", error);
      toast.error("Error looking up reservation");
    } finally {
      setLookingUp(false);
    }
  };

  const getHotelIcon = (hotelId) => {
    const icons = {
      "bird-brain-bungalows": <Bird className="w-6 h-6" />,
      "kinkajoungalows": <Trees className="w-6 h-6" />,
      "susans-cabins": <Sun className="w-6 h-6" />,
    };
    return icons[hotelId] || <Mountain className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen bg-[#F2F0E9]">
      
      {/* Hero Section - Full Screen Immersive */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/28607780/pexels-photo-28607780.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Aerial view of Osa Peninsula rainforest meeting the ocean"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-[#F2F0E9]" />
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center pt-20">
          <p className="font-['Manrope'] uppercase tracking-[0.3em] text-white/80 text-sm mb-6">
            Drake Bay, Costa Rica
          </p>
          <h1 className="font-['Playfair_Display'] text-6xl md:text-8xl lg:text-9xl font-black text-white mb-6 leading-[0.9]">
            Amayas<br/>Corcovado
          </h1>
          <p className="font-['Playfair_Display'] italic text-2xl md:text-3xl text-white/90 mb-12">
            Wild Luxury at the Edge of the World
          </p>
          
          {/* Reservation Code Entry */}
          <div className="max-w-xl mx-auto">
            <form onSubmit={handleCodeLookup} className="relative">
              <div className="flex gap-3 bg-white/20 backdrop-blur-xl p-3 rounded-full border-2 border-white/50 shadow-lg">
                <div className="relative flex-1">
                  <Input
                    value={reservationCode}
                    onChange={(e) => setReservationCode(e.target.value.toUpperCase())}
                    placeholder="Enter Reservation Code"
                    className="bg-transparent border-0 text-white placeholder:text-white/60 text-xl md:text-2xl text-center uppercase tracking-wider focus-visible:ring-0 h-12"
                    data-testid="reservation-code-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={lookingUp}
                  className="bg-[#4A0404] hover:bg-[#2A0202] text-white rounded-full px-8 py-6 font-semibold tracking-wide shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  data-testid="lookup-btn"
                >
                  {lookingUp ? <Loader2 className="w-6 h-6 animate-spin" /> : <ArrowRight className="w-6 h-6" />}
                </Button>
              </div>
              <p className="text-white/60 text-sm mt-3 tracking-wide text-center">
                Example: BBB-A1B2C
              </p>
            </form>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Staff Access - Discrete */}
      <section className="py-6 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-[0.2em] text-black/40 font-['Manrope'] mr-4">Staff Access</span>
            {[
              { to: "/admin", icon: Settings, label: "Admin" },
              { to: "/admin/bird-brain-guests", icon: Users, label: "BBB Guests" },
              { to: "/kitchen", icon: ChefHat, label: "Kitchen" },
              { to: "/housekeeping", icon: Sparkles, label: "Housekeeping" },
              { to: "/tourstaff", icon: Ship, label: "Tours" },
            ].map((item) => (
              <Link key={item.to} to={item.to}>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-black/50 hover:text-[#4A0404] hover:bg-[#4A0404]/5 rounded-full px-4 text-xs uppercase tracking-wider font-['Manrope']"
                  data-testid={`${item.label.toLowerCase().replace(' ', '-')}-btn`}
                >
                  <item.icon className="w-3 h-3 mr-2" />
                  {item.label}
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hotels Section - The Collection */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-16">
            <p className="font-['Manrope'] uppercase tracking-[0.3em] text-[#4A0404] text-sm mb-4">
              Our Properties
            </p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-[#0A0A0A] mb-4">
              The Collection
            </h2>
            <p className="font-['Manrope'] text-lg text-black/60 max-w-xl">
              Eight unique properties, each with its own character. From canopy bungalows to oceanfront cabins.
            </p>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {hotels.map((hotel, index) => {
              const isLarge = index === 0 || index === 3;
              return (
                <Link 
                  key={hotel.id} 
                  to={hotel.cabins.length === 1 
                    ? `/reserve/${hotel.id}/${hotel.cabins[0].id}` 
                    : `/hotel/${hotel.id}`
                  }
                  className={isLarge ? "md:col-span-2" : ""}
                >
                  <Card className={`group relative overflow-hidden bg-white/50 backdrop-blur-sm border border-black/5 hover:border-[#4A0404]/20 transition-all duration-500 ${isLarge ? 'h-80' : 'h-64'}`}>
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-5">
                      <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                      }} />
                    </div>
                    
                    <CardContent className="relative h-full p-6 flex flex-col justify-between">
                      <div className="w-14 h-14 rounded-2xl bg-[#E6E4DC] flex items-center justify-center text-[#0A0A0A] group-hover:bg-[#4A0404] group-hover:text-white transition-colors duration-300">
                        {getHotelIcon(hotel.id)}
                      </div>
                      
                      <div>
                        <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-semibold text-[#0A0A0A] mb-1 group-hover:text-[#4A0404] transition-colors duration-300">
                          {hotel.name}
                        </h3>
                        <p className="font-['Manrope'] text-sm text-black/50">
                          {hotel.cabins.length} {hotel.cabins.length === 1 ? 'cabin' : 'cabins'} available
                        </p>
                      </div>
                      
                      {/* Hover Arrow */}
                      <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <ArrowRight className="w-6 h-6 text-[#4A0404]" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Restaurant Section - La Olla */}
      <section className="py-24 md:py-32 bg-[#E6E4DC] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%234A0404' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Text Content */}
            <div>
              <p className="font-['Manrope'] uppercase tracking-[0.3em] text-[#4A0404] text-sm mb-4">
                Dining Experience
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-[#0A0A0A] mb-4">
                La Olla
              </h2>
              <p className="font-['Playfair_Display'] italic text-xl text-black/60 mb-8">
                "Where the day's adventures become tonight's stories."
              </p>
              <p className="font-['Manrope'] text-lg text-black/70 leading-relaxed mb-8">
                Fresh, locally-sourced meals prepared with love. Join us at the heart of Amayas Corcovado where every dish tells a story.
              </p>
              
              {/* Meal Times */}
              <div className="space-y-4">
                {meals.map((meal) => (
                  <div key={meal.name} className="flex items-center justify-between py-4 border-b border-black/10">
                    <div className="flex items-center gap-4">
                      <Utensils className="w-5 h-5 text-[#4A0404]" />
                      <div>
                        <span className="font-['Playfair_Display'] text-lg font-semibold text-[#0A0A0A]">{meal.name}</span>
                        <span className="font-['Manrope'] text-sm text-black/50 ml-3">{meal.time}</span>
                      </div>
                    </div>
                    <span className="font-['Playfair_Display'] text-xl font-bold text-[#4A0404]">${meal.price}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-4 bg-white/50 rounded-xl">
                <p className="font-['Manrope'] text-sm text-black/60">
                  <Clock className="w-4 h-4 inline mr-2 text-[#4A0404]" />
                  Reservations required 2 hours before meal time
                </p>
              </div>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80"
                  alt="La Olla Restaurant"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-[#4A0404] text-white p-6 rounded-2xl shadow-xl">
                <p className="font-['Manrope'] text-xs uppercase tracking-widest mb-1">Room Service</p>
                <p className="font-['Playfair_Display'] text-2xl font-bold">+$2.50</p>
                <p className="font-['Manrope'] text-xs opacity-70">per person</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tours Section - Expeditions */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="font-['Manrope'] uppercase tracking-[0.3em] text-[#4A0404] text-sm mb-4">
              Adventures Await
            </p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-[#0A0A0A] mb-4">
              Expeditions
            </h2>
            <p className="font-['Manrope'] text-lg text-black/60 max-w-2xl mx-auto">
              From the depths of Corcovado to the waters of Drake Bay. Book through your guest portal.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {tourCategories.map((tour, index) => (
              <Card 
                key={tour.title}
                className="group relative h-96 overflow-hidden bg-white/50 border-0 cursor-pointer"
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img 
                    src={tour.image}
                    alt={tour.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                </div>
                
                <CardContent className="relative h-full p-8 flex flex-col justify-end">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:bg-[#4A0404] transition-colors duration-300">
                    <tour.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-['Manrope'] uppercase tracking-[0.2em] text-white/60 text-xs mb-1">
                    {tour.subtitle}
                  </p>
                  <h3 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
                    {tour.title}
                  </h3>
                  <p className="font-['Manrope'] text-white/70">
                    {tour.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="font-['Playfair_Display'] text-3xl font-bold mb-4">Amayas Corcovado</h3>
              <p className="font-['Manrope'] text-white/60">
                Wild luxury in Drake Bay, Costa Rica. Where the jungle meets the sea.
              </p>
            </div>
            <div>
              <h4 className="font-['Manrope'] uppercase tracking-[0.2em] text-white/40 text-sm mb-4">Quick Links</h4>
              <div className="space-y-2">
                {[
                  { to: "/contact", label: "Contact Us" },
                  { to: "/terms", label: "Terms & Conditions" },
                  { to: "/privacy", label: "Privacy Policy" },
                  { to: "/refund-policy", label: "Refund Policy" },
                ].map((link) => (
                  <Link 
                    key={link.to} 
                    to={link.to}
                    className="block font-['Manrope'] text-white/60 hover:text-[#4A0404] transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-['Manrope'] uppercase tracking-[0.2em] text-white/40 text-sm mb-4">Contact</h4>
              <p className="font-['Manrope'] text-white/60 mb-2">Drake Bay, Osa Peninsula</p>
              <p className="font-['Manrope'] text-white/60 mb-2">Costa Rica</p>
              <p className="font-['Manrope'] text-[#4A0404]">amayascorcovado.com</p>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="font-['Manrope'] text-white/40 text-sm">
              © {new Date().getFullYear()} Amayas Corcovado. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
